// Settings-01 / Settings-01b — Team Members page.
//
// Single-tab Settings shell ahead of the Phase-1 close-out. Future
// passes (locations, roles/permissions, account teams) hang off this
// page, but the current chunks ship Team Members with invite +
// resend/revoke + edit + last-active.

import { useState } from "react";

import { Button } from "@/design-system";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { InviteTeamMemberModal } from "./InviteTeamMemberModal";
import { TeamMemberEditModal } from "./TeamMemberEditModal";
import { TeamMembersTable } from "./TeamMembersTable";
import {
  useInviteTeamMemberMutation,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
  useTeamMembersQuery,
  useTitleOptionsQuery,
  useUpdateTeamMemberMutation,
} from "./hooks";
import {
  invitationIdFor,
  type InviteTeamMemberInput,
  type TeamMember,
  type TeamMemberPatchInput,
} from "./types";


export function SettingsTeamMembersPage() {
  const teamQuery = useTeamMembersQuery();
  const titleOptionsQuery = useTitleOptionsQuery();
  const updateMutation = useUpdateTeamMemberMutation();
  const inviteMutation = useInviteTeamMemberMutation();
  const resendMutation = useResendInvitationMutation();
  const revokeMutation = useRevokeInvitationMutation();

  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<TeamMember | null>(null);
  const [resendingMemberId, setResendingMemberId] =
    useState<string | null>(null);

  const handleSave = async (input: TeamMemberPatchInput) => {
    if (!editing) return;
    setEditError(null);
    try {
      await updateMutation.mutateAsync({
        member_id: editing.member_id,
        values: input,
      });
      setEditing(null);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Couldn't save changes.";
      setEditError(msg);
    }
  };

  const handleInvite = async (input: InviteTeamMemberInput) => {
    setInviteError(null);
    try {
      await inviteMutation.mutateAsync(input);
      setInviteOpen(false);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Couldn't send invitation.";
      setInviteError(msg);
    }
  };

  const handleResend = async (row: TeamMember) => {
    const id = invitationIdFor(row);
    if (!id) return;
    setResendingMemberId(row.member_id);
    try {
      await resendMutation.mutateAsync(id);
    } finally {
      setResendingMemberId(null);
    }
  };

  const handleConfirmRevoke = async () => {
    if (!revoking) return;
    const id = invitationIdFor(revoking);
    setRevoking(null);
    if (!id) return;
    await revokeMutation.mutateAsync(id);
  };

  return (
    <div data-testid="settings-team-members-page" className="flex flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">
            Settings
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-text-secondary">
            Team members for this brokerage. Roles and permissions land
            in a follow-up pass.
          </p>
        </div>
        <Button
          variant="primary"
          data-testid="settings-add-team-member"
          onClick={() => {
            setInviteError(null);
            setInviteOpen(true);
          }}
        >
          + Add team member
        </Button>
      </header>

      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-medium uppercase tracking-wide text-text-tertiary">
          Team members
        </h3>

        {teamQuery.isLoading ? (
          <DashboardLoadingState variant="table" rows={4} />
        ) : teamQuery.isError ? (
          <DashboardErrorState
            title="Couldn't load team members"
            description="Refresh to retry."
            retry={() => teamQuery.refetch()}
          />
        ) : teamQuery.data && teamQuery.data.rows.length > 0 ? (
          <TeamMembersTable
            rows={teamQuery.data.rows}
            resendingMemberId={resendingMemberId}
            onEdit={(row) => {
              setEditing(row);
              setEditError(null);
            }}
            onResend={handleResend}
            onRevoke={(row) => setRevoking(row)}
          />
        ) : (
          <DashboardEmptyState
            title="No team members yet"
            description="Send an invitation to start your team."
          />
        )}
      </section>

      <TeamMemberEditModal
        open={editing !== null}
        member={editing}
        titleOptions={titleOptionsQuery.data?.titles ?? []}
        saving={updateMutation.isPending}
        error={editError}
        onClose={() => {
          setEditing(null);
          setEditError(null);
        }}
        onSave={handleSave}
      />

      <InviteTeamMemberModal
        open={inviteOpen}
        titleOptions={titleOptionsQuery.data?.titles ?? []}
        saving={inviteMutation.isPending}
        error={inviteError}
        onClose={() => {
          setInviteOpen(false);
          setInviteError(null);
        }}
        onSubmit={handleInvite}
      />

      <ConfirmDialog
        open={revoking !== null}
        title="Revoke invitation?"
        body={
          revoking
            ? `The invitation to ${revoking.email} will stop working immediately. They won't be able to accept it.`
            : ""
        }
        confirmLabel="Revoke"
        variant="danger"
        onConfirm={handleConfirmRevoke}
        onCancel={() => setRevoking(null)}
        testId="revoke-invitation-confirm"
      />
    </div>
  );
}
